import { useState, useEffect, useMemo, useRef, Fragment } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Button } from '../components/ui/button';
import { ArchiveIcon, ListIcon } from '../components/ui/icons';
import CategoryFilter from '../components/CategoryFilter';
import { questionApi } from '../api/question.api';
import { adApi } from '../api/ad.api';
import { weightedShuffle, weightedRandomSelect } from '../util/weightedRandom';
import { GridSkeleton, SectionLoading } from '../components/ui/skeleton';
import QuestionCardV2 from '../components/QuestionCardV2';
import AdCard from '../components/ad/AdCard';
import MiniBanner from '../components/ad/MiniBanner';
import { QuestionV2 } from '../types/Question.type';
import { ISlice } from '../types/Page.type';
import { useCardScrollRestore } from '../hooks/useCardScrollRestore';

const SCROLL_ID_KEY = 'dashboardScrollId';

/**
 * 메인 대시보드 화면
 *
 * - 설문목록 노출 (통합 API 사용)
 *
 * @author hjkim
 * @constructor
 */
const Dashboard = () => {
  /**
   * Constants
   */
  const PAGE_SIZE = 6;

  /**
   * States
   */
  /* 카테고리 필터 옵션 state */
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  /* 설문 만료 여부 필터 state */
  const [expirationFilter, setExpirationFilter] = useState<'all' | 'inProgress' | 'expired'>('all');

  /**
   * Refs
   */
  const observeRef = useRef<HTMLDivElement | null>(null);

  /**
   * APIs - 통합 설문 목록 조회 API 사용
   */
  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } = useInfiniteQuery<ISlice<QuestionV2>>({
    queryKey: ['questionList', expirationFilter, selectedCategory],
    queryFn: ({ pageParam = 0 }) => {
      const params = {
        page: pageParam as number,
        size: PAGE_SIZE,
        filterType: 'all' as const,
        categoryId: selectedCategory === 'all' ? undefined : selectedCategory,
        sortByExpired: expirationFilter === 'inProgress' ? false : expirationFilter === 'expired' ? true : undefined,
      };
      return questionApi.getUnifiedQuestionList(params);
    },
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.hasNext ? allPages.length : undefined;
    },
    initialPageParam: 0,
    staleTime: 1000 * 60,
  });

  // 무한 스크롤 Intersection Observer
  useEffect(() => {
    if (!observeRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 1.0 },
    );
    observer.observe(observeRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const questionListData = data?.pages.flatMap((page) => page.content) ?? [];
  const isInitialLoading = isFetching && !isFetchingNextPage;

  // 클릭한 카드 위치로 스크롤 복원 (뒤로가기 시)
  const { saveCardId } = useCardScrollRestore({
    itemCount: questionListData.length,
    storageKey: SCROLL_ID_KEY,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  /** 메인 피드 광고 조회 */
  const { data: adsData } = useQuery({
    queryKey: ['ads', 'MAIN_FEED'],
    queryFn: () => adApi.getAdsByPosition('MAIN_FEED').then((res) => res.data),
    staleTime: 1000 * 60 * 5,
  });
  const feedAds = useMemo(() => {
    if (!adsData || adsData.length === 0) return [];
    return weightedShuffle(adsData);
  }, [adsData]);

  /** 피드 상단 미니배너 광고 조회 */
  const { data: topAdsData } = useQuery({
    queryKey: ['ads', 'MAIN_FEED_TOP'],
    queryFn: () => adApi.getAdsByPosition('MAIN_FEED_TOP').then((res) => res.data),
    staleTime: 1000 * 60 * 5,
  });
  const topAd = useMemo(() => {
    if (!topAdsData || topAdsData.length === 0) return undefined;
    return weightedRandomSelect(topAdsData);
  }, [topAdsData]);

  /**
   * Handlers
   */
  /* 카테고리 필터 옵션 선택 handler */
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
  };

  /* 설문 만료 여부 필터 변경 handler */
  const handleExpirationFilterChange = (filter: 'all' | 'inProgress' | 'expired') => {
    setExpirationFilter(filter);
  };

  return (
    <main className="flex-1 max-w-6xl mx-auto px-1 sm:px-2 w-full">
      {/* 피드 상단 미니배너 (탭/필터 위) */}
      {topAd && (
        <div className="mb-4">
          <MiniBanner ad={topAd} position="MAIN_FEED_TOP" />
        </div>
      )}
      <CategoryFilter selectedCategory={selectedCategory} handleCategoryChange={handleCategoryChange} />
      {/* Sorting */}
      <div className="flex flex-col mb-4 mt-1 sm:mb-6">
        <div className="flex items-center justify-between mb-3 px-1">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold text-gray-900">설문 목록</h2>
          </div>
          <div className="text-sm text-gray-600">
            <span className="font-medium">{questionListData.length}</span>
            <span className="ml-1">개의 설문</span>
          </div>
        </div>

        {/* 진행/종료 구분 - 모바일: 탭, 데스크톱: 버튼 */}
        <div className="w-full sm:w-auto">
          {/* 모바일 탭 버전 */}
          <div className="flex sm:hidden w-full border rounded-lg overflow-hidden">
            <button
              type="button"
              className={`flex-1 py-2.5 px-4 text-center text-sm font-medium ${
                expirationFilter === 'all' ? 'bg-primary text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
              onClick={() => handleExpirationFilterChange('all')}
            >
              전체
            </button>
            <button
              type="button"
              className={`flex-1 py-2.5 px-4 text-center text-sm font-medium border-x ${
                expirationFilter === 'inProgress' ? 'bg-primary text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
              onClick={() => handleExpirationFilterChange('inProgress')}
            >
              진행중
            </button>
            <button
              type="button"
              className={`flex-1 py-2.5 px-4 text-center text-sm font-medium ${
                expirationFilter === 'expired' ? 'bg-primary text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
              onClick={() => handleExpirationFilterChange('expired')}
            >
              종료됨
            </button>
          </div>

          {/* 데스크톱 버튼 버전 */}
          <div className="hidden sm:flex items-center gap-2">
            <Button
              type="button"
              variant={expirationFilter === 'all' ? 'default' : 'outline'}
              size="sm"
              className="flex items-center gap-2"
              onClick={() => handleExpirationFilterChange('all')}
            >
              <ListIcon className="w-4 h-4" />
              전체
            </Button>
            <Button
              type="button"
              variant={expirationFilter === 'inProgress' ? 'default' : 'outline'}
              size="sm"
              className="flex items-center gap-2"
              onClick={() => handleExpirationFilterChange('inProgress')}
            >
              <div className="w-2 h-2 rounded-full bg-green-500" />
              진행중
            </Button>
            <Button
              type="button"
              variant={expirationFilter === 'expired' ? 'default' : 'outline'}
              size="sm"
              className="flex items-center gap-2"
              onClick={() => handleExpirationFilterChange('expired')}
            >
              <ArchiveIcon className="w-4 h-4" />
              종료됨
            </Button>
          </div>
        </div>
      </div>
      {/* 설문 목록 - 로딩 */}
      <SectionLoading
        isLoading={isInitialLoading}
        skeleton={
          <GridSkeleton
            count={6}
            columns={{
              default: 1,
              md: 2,
              lg: 3,
            }}
            variant="card"
          />
        }
      >
        {/* 설문 목록 */}
        <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {questionListData.length === 0 ? (
            <div className="description-center py-12">
              <p className="mb-4">설문이 없습니다.</p>
              <Button>첫 설문 만들기</Button>
            </div>
          ) : (
            questionListData.map((question: QuestionV2, index: number) => (
              <Fragment key={question.questionId}>
                <div
                  id={`q-${question.questionId}`}
                  onClick={() => saveCardId(question.questionId)}
                  className="outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-lg transition-all h-full"
                >
                  <QuestionCardV2 question={question} />
                </div>
                {/* 3개(한 행)마다 광고 삽입 */}
                {(index + 1) % 3 === 0 && feedAds.length > 0 && (
                  <div className="col-span-full">
                    <AdCard ad={feedAds[Math.floor(index / 3) % feedAds.length]} position="MAIN_FEED" />
                  </div>
                )}
              </Fragment>
            ))
          )}
        </div>
      </SectionLoading>
      {/* 무한 스크롤 옵저버 */}
      <div ref={observeRef} style={{ height: '50px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        {isFetchingNextPage && <p>Loading more...</p>}
      </div>
    </main>
  );
};
export default Dashboard;
